/**
 * TaskModuleContext - Escopo de módulo de tarefas fora das rotas dedicadas
 *
 * O módulo de Tarefas atende três tipos (task_type_enum): normal | retention | legal.
 * Nas telas dedicadas o tipo é derivado da URL (useTaskModule). Porém, dentro dos modais
 * de Cliente / Negócio / Veículo / Chat a rota é outra e tudo cairia em "normal".
 *
 * Este contexto permite que um trecho da árvore declare explicitamente o módulo corrente.
 * Quando presente, ele tem PRIORIDADE sobre a rota — e todos os hooks derivados
 * (useTaskCategories, useTaskPhases, useTaskCreationOptions, TaskFormDialog, listas)
 * passam a operar no tipo correto sem prop drilling.
 *
 * Obs.: importa apenas o TIPO de useTaskModuleType (import type é apagado na compilação),
 * evitando ciclo de import em runtime — o hook é quem importa este contexto.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-21 v1.0.0 - Criação inicial (subabas por tipo nos modais)
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

/** Valor do contexto: tipo forçado (ou null quando a rota deve prevalecer) */
const TaskModuleContext = createContext<TaskModuleType | null>(null);

interface TaskModuleProviderProps {
  /** Tipo do módulo que deve valer para toda a subárvore */
  type: TaskModuleType;
  children: ReactNode;
}

/**
 * Provider que força o módulo de tarefas da subárvore.
 */
export function TaskModuleProvider({ type, children }: TaskModuleProviderProps) {
  return <TaskModuleContext.Provider value={type}>{children}</TaskModuleContext.Provider>;
}

/**
 * Retorna o override do módulo (ou null quando não há override ativo).
 * Usado internamente por useTaskModule().
 */
export function useTaskModuleOverride(): TaskModuleType | null {
  return useContext(TaskModuleContext);
}
